import * as React from "react";
import { ChevronsUpDown, Plus, Building2, Check } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useRouter } from "@tanstack/react-router";
import { useSession } from "@/frontend/auth-client";
import { orpc } from "@/frontend/orpc-client";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/frontend/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/frontend/components/ui/sidebar";
import { OnboardingPageLayout } from "../shared/OnboardingPageLayout";
import { PersonaWorkspaceForm } from "../shared/PersonaWorkspaceForm";
import { EnvironmentToggle } from "./EnvironmentToggle";
import {
  ENVIRONMENT_TOGGLE_OPTIONS,
  ORG_ENVIRONMENT_LABEL,
  WORKSPACE_HOME_PATH,
} from "@/frontend/types/org";
import { groupEntryWorkspace, groupWorkspaces } from "@/frontend/utils/dashboard/workspaces";

/**
 * Workspace switcher. A paired workspace's environment switch shows in exactly
 * one place, the inline toggle when the rail is expanded and dropdown items when
 * it is collapsed. "Create Workspace" runs the same setup flow as signup.
 */
export function TeamSwitcher() {
  const { isMobile, state: sidebarState } = useSidebar();
  const { data: session } = useSession();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const router = useRouter();
  const [creatingWorkspace, setCreatingWorkspace] = React.useState(false);
  const isPlatformAdmin = session?.user?.role === "admin";

  const organizationsQuery = useQuery(orpc.org.listOrganizations.queryOptions({ input: {} }));
  const activeOrgQuery = useQuery(orpc.org.getFullOrganization.queryOptions({ input: {} }));
  const switchMutation = useMutation(
    orpc.org.setActiveOrganization.mutationOptions({
      onSuccess: async (activeOrganization) => {
        // The switch already returned the new workspace, so the route guard reads it without a refetch.
        queryClient.setQueryData(
          orpc.org.getFullOrganization.queryKey({ input: {} }),
          activeOrganization,
        );
        // Off-screen pages cached for the old workspace must not render under the new one.
        queryClient.removeQueries({ type: "inactive" });
        // Refetch on-screen data and load the home page together instead of one after the other.
        await Promise.all([
          queryClient.invalidateQueries(),
          navigate({ to: WORKSPACE_HOME_PATH, search: {} }),
        ]);
        await router.invalidate();
      },
    })
  );

  const organizations = organizationsQuery.data ?? [];
  const activeOrg = activeOrgQuery.data;
  const activeId = React.useMemo(() => {
    if (activeOrg?.id) return activeOrg.id;
    if (organizations.length === 1) return organizations[0]?.id;
    return undefined;
  }, [activeOrg, organizations]);

  const currentOrg = organizations.find((org) => org.id === activeId);
  const workspaceGroups = React.useMemo(() => groupWorkspaces(organizations), [organizations]);
  const activeGroup = workspaceGroups.find((group) =>
    group.members.some((member) => member.id === activeId),
  );
  const canSwitchOrgs = isPlatformAdmin || workspaceGroups.length > 1;
  const canSwitchEnvironment = !!activeGroup?.isPaired && !!currentOrg;
  const isRailExpanded = isMobile || sidebarState === "expanded";
  const showInlineToggle = canSwitchEnvironment && isRailExpanded;
  const showDropdownEnvironments = canSwitchEnvironment && !isRailExpanded;

  return (
    <>
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                size="lg"
                className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
              >
                {currentOrg?.logo ? (
                  <img
                    src={currentOrg.logo}
                    alt={currentOrg.name}
                    className="aspect-square size-8 shrink-0 rounded-lg object-cover object-center"
                  />
                ) : (
                  <div className="flex aspect-square size-8 shrink-0 items-center justify-center rounded-lg bg-gray-200">
                    <Building2 className="size-5 text-gray-500" />
                  </div>
                )}
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium">
                    {currentOrg?.name || "Select Workspace"}
                  </span>
                  <span className="truncate text-xs text-muted-foreground h-4">
                    {isPlatformAdmin ? "Platform Admin" : currentOrg ? ORG_ENVIRONMENT_LABEL[currentOrg.environment] : activeOrgQuery.isLoading ? null : "Workspace"}
                  </span>
                </div>
                <ChevronsUpDown className="ml-auto" />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
              align="start"
              side={isMobile ? "bottom" : "bottom"}
              sideOffset={4}
            >
              {showDropdownEnvironments && activeGroup && currentOrg && (
                <>
                  <DropdownMenuLabel className="text-caption text-muted-foreground">
                    Environment
                  </DropdownMenuLabel>
                  {ENVIRONMENT_TOGGLE_OPTIONS.map((option) => {
                    const sibling = activeGroup.members.find(
                      (member) => member.environment === option.value,
                    );
                    if (!sibling) return null;
                    const isActiveEnvironment = sibling.id === currentOrg.id;
                    return (
                      <DropdownMenuItem
                        key={option.value}
                        className="gap-2 p-2"
                        onClick={() => {
                          if (!isActiveEnvironment) {
                            switchMutation.mutate({ organizationId: sibling.id });
                          }
                        }}
                      >
                        <span className="flex-1">{option.label}</span>
                        {isActiveEnvironment && <Check className="size-4" />}
                      </DropdownMenuItem>
                    );
                  })}
                  <DropdownMenuSeparator />
                </>
              )}
              {canSwitchOrgs && (
                <>
                  <DropdownMenuLabel className="text-xs text-muted-foreground">
                    Workspaces
                  </DropdownMenuLabel>
                  {workspaceGroups.map((group) => {
                    const row = group.primary;
                    const isActiveGroup = group.key === activeGroup?.key;
                    return (
                      <DropdownMenuItem
                        key={group.key}
                        onClick={() => {
                          // Moving within a group is the environment switch's job.
                          if (!isActiveGroup) switchMutation.mutate({ organizationId: groupEntryWorkspace(group).id });
                        }}
                        className="gap-2 p-2"
                      >
                        {row.logo ? (
                          <img
                            src={row.logo}
                            alt={row.name}
                            className="aspect-square size-6 shrink-0 rounded-md object-cover object-center"
                          />
                        ) : (
                          <div className="flex aspect-square size-6 shrink-0 items-center justify-center rounded-md bg-muted">
                            <Building2 className="size-4 text-muted-foreground" />
                          </div>
                        )}
                        <span className="flex-1">
                          {row.name}
                          {/* Paired workspaces show the environment on the toggle instead. */}
                          {!group.isPaired && row.environment && (
                            <span className="ml-1 text-caption text-muted-foreground">
                              ({ORG_ENVIRONMENT_LABEL[row.environment]})
                            </span>
                          )}
                        </span>
                        {isActiveGroup && <Check className="size-4" />}
                      </DropdownMenuItem>
                    );
                  })}
                  <DropdownMenuSeparator />
                </>
              )}
              <DropdownMenuItem
                className="gap-2 p-2"
                onClick={() => setCreatingWorkspace(true)}
              >
                <div className="flex size-6 items-center justify-center rounded-md border bg-transparent">
                  <Plus className="size-4" />
                </div>
                <span className="font-medium text-muted-foreground">Create Workspace</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {showInlineToggle && activeGroup && currentOrg && (
            <div className="px-2 pt-1.5">
              <EnvironmentToggle
                siblings={activeGroup.members}
                activeEnvironment={currentOrg.environment}
                disabled={switchMutation.isPending}
                onSelect={(organizationId) => switchMutation.mutate({ organizationId })}
              />
            </div>
          )}
        </SidebarMenuItem>
      </SidebarMenu>

      {creatingWorkspace ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Create workspace"
          className="fixed inset-0 z-50 overflow-y-auto bg-background"
        >
          <OnboardingPageLayout>
            <PersonaWorkspaceForm
              onCompleted={() => window.location.assign(WORKSPACE_HOME_PATH)}
              onCancel={() => setCreatingWorkspace(false)}
            />
          </OnboardingPageLayout>
        </div>
      ) : null}
    </>
  );
}
